<template>
    <div>
        <h1 class="font-body-6 text-default-2 xxs:pl-4 lg:pl-0">Documents</h1>
        <div class="flex w-full">
            <div class="xxs:p-4 bg-white rounded border border-white lg:px-6 lg:pt-4 lg:pb-6 mt-4 box-border flex-1 max-w-[calc(100% - 250px)] xxs:overflow-visible mlg:overflow-hidden">
                <div v-if="loadingPage" class="w-full flex flex-col box-border">
                    <div class="w-full flex xxs:flex-col lg:flex-row mb-5">
                        <div class="skeleton-loader xxs:w-full lg:w-3/5 h-[42px]"></div>
                        <div class="flex-1"></div>
                        <div class="skeleton-loader xxs:w-full md:w-full lg:w-1/6 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                        <div class="skeleton-loader xxs:w-full md:w-full lg:w-1/6 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    </div>
                    <div class="skeleton-loader w-full mt-1 h-[40px]" v-for="item in perPage - 1" :key="item"></div>
                </div>
                <div v-else class="w-full max-w-full flex flex-col">
                    <div v-if="hasDocuments || isAdmin">
                        <div class="w-full flex flex-col justify-between lg:flex-row mt-2 mlg:mb-2">
                            <div class="w-full mb-2 mlg:mb-0 xxs:w-full lg:w-[45%] mlg:w-[50%]">
                                <CoreInput
                                    v-model="search"
                                    hasSearchIcon
                                    label="Search"
                                    placeholder="Search in all folders"
                                    @input="handleDebouncedSearch()"
                                >
                                    <div class="absolute inset-y-0 right-0 flex justify-end pr-2 box-border mr-0.5 items-center h-8 w-[50px] mt-1 cursor-pointer grad-bg">
                                        <img v-if="!search" alt="Search through members" src="@/assets/icons/search.svg" class="w-5" />
                                        <img v-else src="@/assets/icons/close-light.svg" @click="clearSearchInput" alt="clear" class="w-5" />
                                    </div>
                                </CoreInput>
                            </div>
                            <div class="flex-1 flex justify-end items-end flex-row">
                                <Menu v-if="SHOW_VIEW_AS_FEATURE" as="div" class="relative inline-block text-left">
                                    <div>
                                        <MenuButton
                                            class="inline-flex w-full justify-center rounded-md px-4 py-2.5 text-body-6 text-default-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
                                        >
                                            View
                                            <ChevronDownIcon
                                                class="-mr-1 ml-2 h-5 w-5 text-default-1 hover:text-violet-100"
                                                aria-hidden="true"
                                            />
                                        </MenuButton>
                                    </div>

                                    <transition
                                        enter-active-class="transition duration-100 ease-out"
                                        enter-from-class="transform scale-95 opacity-0"
                                        enter-to-class="transform scale-100 opacity-100"
                                        leave-active-class="transition duration-75 ease-in"
                                        leave-from-class="transform scale-100 opacity-100"
                                        leave-to-class="transform scale-95 opacity-0"
                                    >
                                        <MenuItems
                                            class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                                        >
                                            <div class="px-1 py-1">
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="handleChangeView('list')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-body-6',
                                                        ]">
                                                        <Check
                                                            v-if="isFilterChecked('list')"
                                                            :color="'#000000'"
                                                            class="w-10 pr-4 flex-shrink-0"
                                                        />
                                                        <span :class="[!isFilterChecked('list') && 'pl-10']">List</span>
                                                    </button>
                                                </MenuItem>
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="handleChangeView('thumbnail')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                                                        ]">
                                                        <Check
                                                            v-if="isFilterChecked('thumbnail')"
                                                            :color="'#000000'"
                                                            class="w-10 pr-4 flex-shrink-0"
                                                        />
                                                        <span :class="[!isFilterChecked('thumbnail') && 'pl-10']">Thumbnail</span>
                                                    </button>
                                                </MenuItem>
                                            </div>
                                        </MenuItems>
                                    </transition>
                                </Menu>
                                <Menu as="div" class="relative inline-block text-left">
                                    <div>
                                        <MenuButton
                                            class="inline-flex w-full justify-center rounded-md px-4 py-2.5 text-sm text-default-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
                                        >
                                            Sorting by
                                            <ChevronDownIcon
                                                class="-mr-1 ml-2 h-5 w-5 text-default-1 hover:text-violet-100"
                                                aria-hidden="true"
                                            />
                                        </MenuButton>
                                    </div>

                                    <transition
                                        enter-active-class="transition duration-100 ease-out"
                                        enter-from-class="transform scale-95 opacity-0"
                                        enter-to-class="transform scale-100 opacity-100"
                                        leave-active-class="transition duration-75 ease-in"
                                        leave-from-class="transform scale-100 opacity-100"
                                        leave-to-class="transform scale-95 opacity-0"
                                    >
                                        <MenuItems
                                            class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-10"
                                        >
                                            <div class="px-1 py-1">
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="doSortBy('title')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                                                        ]">
                                                        <Check v-if="isFilterChecked('title')" :color="'#000000'" class="w-10 pr-4 flex-shrink-0" />
                                                        <span :class="[!isFilterChecked('title') && 'pl-10']">Name</span>
                                                    </button>
                                                </MenuItem>
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="doSortBy('filesize')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                                                        ]">
                                                        <Check v-if="isFilterChecked('filesize')" :color="'#000000'" class="w-10 pr-4 flex-shrink-0" />
                                                        <span :class="[!isFilterChecked('filesize') && 'pl-10']">Size</span>
                                                    </button>
                                                </MenuItem>
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="doSortBy('mime_type')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                                                        ]">
                                                        <Check v-if="isFilterChecked('mime_type')" :color="'#000000'" class="w-10 pr-4 flex-shrink-0" />
                                                        <span :class="[!isFilterChecked('mime_type') && 'pl-10']">Type</span>
                                                    </button>
                                                </MenuItem>
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="doSortBy('created_at')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                                                        ]">
                                                        <Check v-if="isFilterChecked('created_at')" :color="'#000000'" class="w-10 pr-4 flex-shrink-0" />
                                                        <span :class="[!isFilterChecked('created_at') && 'pl-10']">Date</span>
                                                    </button>
                                                </MenuItem>
                                            </div>
                                        </MenuItems>
                                    </transition>
                                </Menu>
                                <Menu as="div" class="relative inline-block text-left" v-if="isAdmin">
                                    <div>
                                        <MenuButton
                                            class="inline-flex w-full bg-primary-1 justify-center rounded-md px-4 py-2.5 text-body-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
                                            :class="textColorClass"
                                        >
                                            + Add
                                        </MenuButton>
                                    </div>

                                    <transition
                                        enter-active-class="transition duration-100 ease-out"
                                        enter-from-class="transform scale-95 opacity-0"
                                        enter-to-class="transform scale-100 opacity-100"
                                        leave-active-class="transition duration-75 ease-in"
                                        leave-from-class="transform scale-100 opacity-100"
                                        leave-to-class="transform scale-95 opacity-0"
                                    >
                                        <MenuItems
                                            class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none max-w-[100px] z-[9999]"
                                        >
                                            <div class="px-1 py-1">
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="handleAdd('folder')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-body-6',
                                                        ]">
                                                        Folder
                                                    </button>
                                                </MenuItem>
                                                <MenuItem v-slot="{ active }">
                                                    <button
                                                        @click="handleAdd('file')"
                                                        :class="[
                                                          active ? `bg-primary-1 ${textColorClass}` : 'text-default-1',
                                                          'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                                                        ]">
                                                        File
                                                    </button>
                                                </MenuItem>
                                            </div>
                                        </MenuItems>
                                    </transition>
                                </Menu>
                            </div>
                        </div>
                        <div class="w-full flex">
                            <DocumentsBreadcrumbs
                                :breadcrumbs="breadCrumbs"
                                @navigate="navigateThroughBreadcrumbs"
                            />
                        </div>
                        <div class="w-full flex items-center justify-end">
                            <CoreButton
                                v-if="fileToBePreviewed"
                                class="w-full mt-4 self-end max-w-[120px]"
                                @click="handleDownload(fileToBePreviewed)"
                                :disabled="downloadingFileLoader"
                                :loading="downloadingFileLoader"
                                label="Download file"
                                size="small"
                                buttonType="secondary"
                                :loaderColor="fullConfig.theme.colors.primary[1]"
                            />
                        </div>
                        <div class="w-full mt-6" v-if="!fileToBePreviewed">
                            <Checkbox
                                :model-value="allDocumentsSelected"
                                @click="selectAllDocuments"
                                label="Select all"
                            />
                        </div>
                    </div>
                    <div class="w-full max-w-full overflow-x-scroll" v-if="fileToBePreviewed">
                        <PreviewDocument
                            :document="fileToBePreviewed"
                        />
                    </div>
                    <div v-if="!fileToBePreviewed && !emptyState">
                        <div v-if="loadingDocuments" class="w-full flex flex-col box-border mt-4" >
                            <div class="skeleton-loader w-full mt-1 xxs:h-[200px] lg:h-[50px]" v-for="item in perPage - 1" :key="item"></div>
                        </div>
                        <div v-if="!loadingDocuments && hasDocuments" class="flex w-full flex-col xxs:pb-3 mlg:pb-0 lg:pt-4 mlg:min-h-[500px] mlg:max-h-[calc(100vh-380px)] lg:overflow-y-scroll xxs:overflow-x-visible" >
                            <DocumentRow
                                v-for="(document, index) in documents"
                                :index="index"
                                :total="documents.length"
                                :key="document.id"
                                :document="document"
                                :selected-documents="selectedDocuments"
                                :is-all-selected="allDocumentsSelected"
                                @action="handleAction"
                            />
                            <InfiniteScrollObserver
                                v-if="!infiniteLoader && currentPage < meta.last_page"
                                @intersect="checkInViewport">
                            </InfiniteScrollObserver>
                            <div v-if="infiniteLoader" class="w-full flex flex-col box-border" >
                                <div class="skeleton-loader w-full mt-1 xxs:h-[200px] lg:h-[42px]" v-for="item in 3" :key="item"></div>
                            </div>
                        </div>
                    </div>
                    <div v-if="!fileToBePreviewed && emptyState" class="w-full h-[650px] flex justify-center items-center flex-col">
                        <ItemsListWithDownloadIcon :color="color.hex" />
                        <p class="mt-8 text-body-4 text-default-1 text-center">{{ emptyStateText }}</p>
                        <p class="mt-4 text-body-6 text-default-2 text-center">
                            {{ emptyStateSubtitle }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <AddFolderModal
        v-if="addFolderModalVisibility"
        :current-folder="currentFolder"
        :modal-visibility="addFolderModalVisibility"
        @close="addFolderModalVisibility = false"
        @add-folder="addFolder"
        :loading="addFolderLoading"
    />
    <AddFileModal
        :current-folder="currentFolder"
        :modal-visibility="addFileModalVisibility"
        @close="addFileModalVisibility = false"
        @file-uploaded="refreshData"
    />
    <BulkActions
        v-if="allDocumentsSelected || selectedDocuments.include.length > 0"
        :selected-documents="selectedDocuments"
        :total="totalDocuments"
        @action="handleAction"
    />
    <DeleteConfirmationModal
        :modal-visibility="deleteModalVisibility"
        :loading="deleteLoading"
        :single-file="singleDocumentToBeDeleted"
        :selected-documents="selectedDocuments"
        :total-documents="totalDocuments"
        @close="closeDeleteConfirmationModal"
        @delete="handleDelete"
    />
    <Modal
        :show="shareModalVisibility"
        @close="handleAfterLeaveShareModal"
        class="overflow-visible"
        :overflow-hidden="false"
        @after-leave="handleAfterLeaveShareModal"
    >
        <Share
            type="document"
            :allow-unshare="true"
            :members="singleDocumentToBeShared ? singleDocumentToBeShared.members : []"
            :groups="singleDocumentToBeShared ? singleDocumentToBeShared.groups : []"
            :modal-visibility="shareModalVisibility"
            @close="shareModalVisibility = false"
            @share="handleShare"
        >
            <ShareLink :document="singleDocumentToBeShared" />
        </Share>
    </Modal>
    <MoveDocumentToFolderModal
        v-if="moveDocumentModalVisibility"
        :modal-visibility="moveDocumentModalVisibility"
        :selected-documents="selectedDocuments"
        :single-file="singleDocumentToBeMoved"
        @close="closeMoveDocumentModal"
        @move="handleMoveDocument"
        :movingLoader="moveDocumentLoading"
    />
</template>

<script setup>
import {useDocuments} from "@/composables/useDocuments";
import CoreInput from "@/components/core/InputGroup.vue";
import {computed, onBeforeMount, ref} from "vue";
import debounce from "lodash.debounce";
import DocumentRow from "@/components/custom/documents/DocumentRow.vue";
import {useTheme} from "@/stores/ThemeStore";
import ItemsListWithDownloadIcon from "@/components/icons/ItemsListWithDownloadIcon.vue";
import { ChevronDownIcon } from '@heroicons/vue/solid'
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/vue';
import Check from "@/components/icons/Check.vue";
import AddFolderModal from "@/components/custom/documents/AddFolderModal.vue";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import AddFileModal from "@/components/custom/documents/AddFileModal.vue";
import InfiniteScrollObserver from "@/components/InfiniteScrollObserver.vue";
import Checkbox from "@/components/core/Checkbox.vue";
import BulkActions from "@/components/custom/documents/BulkActions.vue";
import DeleteConfirmationModal from "@/components/custom/documents/DeleteConfirmationModal.vue";
import DocumentsBreadcrumbs from "@/components/custom/documents/DocumentsBreadcrumbs.vue";
import Modal from "@/components/core/Modal.vue";
import Share from "@/components/modals/partials/Share.vue";
import MoveDocumentToFolderModal from "@/components/custom/documents/MoveDocumentToFolderModal.vue";
import {useFiles} from "@/composables/useFiles";
import {toast} from "vue3-toastify";
import PreviewDocument from "@/components/custom/documents/PreviewDocument.vue";
import {useRoute} from "vue-router";
import ShareLink from "@/components/custom/documents/DocumentShareLink.vue";
import CoreButton from "@/components/core/Button.vue";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../../tailwind.config";
import {useIncludeExcludeOptions} from "@/composables/useIncludeExcludeOptions";
import {useDomPurifier} from "@/composables/useDomPurifier";

const fullConfig = resolveConfig(tailwindConfig)

const route = useRoute();
const sharedLinkType = route.params.type; // f = folder, d = document
const sharedLinkUid = route.params.uid;

const {organization, isAdmin} = storeToRefs(useAuth());

const {color, textColorClass} = useTheme()

const {createFolder, list, addToFavorite, removeFromFavorite, deleteDocuments, shareDocuments, moveDocuments, showFile} = useDocuments();
const {downloadFile} = useFiles();

const {resetIncludeExclude} = useIncludeExcludeOptions();

const loadingPage = ref(false);
const loadingDocuments = ref(false);
const infiniteLoader = ref(false);
const currentPage = ref(1);

const documents = ref([]);
const selectedDocuments = ref({
    include: [],
    exclude: []
});
const allDocumentsSelected = ref(false);
const meta = ref(null);
const links = ref([]);

const SHOW_VIEW_AS_FEATURE = false;

const search = ref('');
const perPage = 10;
const sortBy = ref('title');
const viewAs = ref('list');

const addFileModalVisibility = ref(false);
const addFolderModalVisibility = ref(false);
const addFolderLoading = ref(false);

const deleteModalVisibility = ref(false);
const deleteLoading = ref(false);

const moveDocumentModalVisibility = ref(false);
const moveDocumentLoading = ref(false);

const singleDocumentToBeDeleted = ref(null);
const singleDocumentToBeShared = ref(null);
const singleDocumentToBeMoved = ref(null);

const hasDocuments = ref(false);

const handleDebouncedSearch = debounce(handleSearch, 300);

const breadCrumbs = ref([]);

const DEFAULT_FOLDERS = ['all_files', 'favorite', 'share', 'bin']
const FavoriteOrSharedFilter = ref(null);

const shareModalVisibility = ref(false);
const shareLoading = ref(false);

const fileToBePreviewed = ref(null);
const loadingPreviewFile = ref(false);

const downloadingFileLoader = ref(false);

async function handleSearch() {
    // todo: title filter
    loadingDocuments.value = true;

    currentPage.value = 1;
    selectedDocuments.value = {
        include: [],
        exclude: []
    }
    breadCrumbs.value = [];

    await loadData(true, null);

    loadingDocuments.value = false;
}

const clearSearchInput = () => {
    search.value = '';
    handleSearch();
}

async function doSortBy(type) {
    sortBy.value = type;

    currentPage.value = 1;
    selectedDocuments.value = {
        include: [],
        exclude: []
    }

    loadingDocuments.value = true;

    await loadData(true, currentFolderUid.value);

    loadingDocuments.value = false;
}

function handleChangeView(type) {
    viewAs.value = type;
}

function handleAdd(type) {
    if(type === 'file') {
        addFileModalVisibility.value = true;
        return;
    }
    addFolderModalVisibility.value = true;
}

function isFilterChecked(type) {
    return viewAs.value === type || sortBy.value === type;
}

async function addFolder(folderName) {
    addFolderLoading.value = true;

    let data = new FormData();
    data.append('title', folderName);

    if(currentFolder.value) {
        data.append('folder_uid', currentFolderUid.value);
    }

    try {
        await createFolder(organization.value.slug, data);

        await refreshData();
    } catch (e) {
        if(e.response.status === 409) {
            return toast.error(e.response.data.message);
        }
        toast.error('An error occurred while creating the folder. Please try again');
    } finally {
        addFolderModalVisibility.value = false;
        addFolderLoading.value = false;
    }
}

async function loadInitialData(folderUid) {
    loadingPage.value = true;

    await loadData(true, folderUid);

    loadingPage.value = false;
}

async function loadData(clearData = false, folderUid = null) {
    try {
        if(clearData) {
            documents.value = [];
        }

        const response = await list(organization.value.slug, currentPage.value, perPage, search.value, sortBy.value, folderUid);
        documents.value = [...documents.value, ...response.data];

        documents.value = documents.value.map(doc => {
            doc.documentable.title = useDomPurifier(doc.documentable.title);
            return doc;
        });

        meta.value = response.meta;
        links.value = response.links;

        return Promise.resolve(documents.value);
    } catch (e) {
        toast.error('An error occurred while fetching the documents');
        return Promise.reject();
    }
}

async function loadMoreData() {
    infiniteLoader.value = true;

    await loadData(false, currentFolderUid.value);

    infiniteLoader.value = false;
}

async function navigateThroughBreadcrumbs(folderUid) {
    if(loadingDocuments.value) {
        return;
    }

    loadingDocuments.value = true;

    const index = breadCrumbs.value.findIndex(bread => bread.documentable.uid === folderUid);

    if(index < breadCrumbs.value.length) {
        breadCrumbs.value = breadCrumbs.value.slice(0, index + 1);
    }

    currentPage.value = 1;
    fileToBePreviewed.value = null;
    selectedDocuments.value = {
        include: [],
        exclude: []
    }
    search.value = '';

    if(DEFAULT_FOLDERS.includes(folderUid)) {
        FavoriteOrSharedFilter.value = folderUid;

        if(folderUid === 'all_files') {
            breadCrumbs.value = [];
            await loadData(true);
            loadingDocuments.value = false;

            return;
        }

        await loadData(true, null, folderUid);

        loadingDocuments.value = false;

        return;
    }

    await loadData(true, folderUid);

    loadingDocuments.value = false;
}

async function navigateToFolder(folderUid) {
    if(loadingDocuments.value) {
        return;
    }

    search.value = ''; // this allows us to navigate into folders after we have searched, and actually see the items that were in the searched folder. Otherwise, without clearing the search query, the db query for the folder you are about to access will contain the search string and return accordingly

    loadingDocuments.value = true;

    try {
        const index = documents.value.findIndex(doc => doc.documentable.uid === folderUid);
        breadCrumbs.value.push(documents.value[index]);

        currentPage.value = 1;
        selectedDocuments.value = {
            include: [],
            exclude: []
        }

        await loadData(true, folderUid);
    } catch (e) {
        toast.error('An error occurred while navigating to the folder');
        return Promise.reject();
    } finally {
        loadingDocuments.value = false;
    }
}

async function previewFile(file) {
    loadingPreviewFile.value = true;

    fileToBePreviewed.value = file;
    const index = documents.value.findIndex(doc => doc.documentable.uid === file.documentable.uid);
    breadCrumbs.value.push(documents.value[index]);

    loadingPreviewFile.value = false;
}

async function refreshData() {
    loadingDocuments.value = true;

    search.value = '';
    currentPage.value = 1;
    resetSelectedDocuments();

    await loadData(true, currentFolderUid.value);

    hasDocuments.value = documents.value.length > 0;

    loadingDocuments.value = false;
}

function resetSelectedDocuments() {
    selectedDocuments.value = {
        include: [],
        exclude: []
    }
}

async function handleDelete() {
    deleteLoading.value = true;

    const filesToBeDeleted = {
        documentables: {
            include: [],
            exclude: []
        }
    };

    if (currentFolderUid.value) {
        filesToBeDeleted.parent_documentable_uid = currentFolderDocumentableUid.value;
    }

    const spliceDocuments = (uids) => {
        uids.forEach(uid => {
            const index = documents.value.findIndex(doc => doc.uid === uid);
            if (index !== -1) {
                documents.value.splice(index, 1);
            }
        });
    };

    if (singleDocumentToBeDeleted.value) {
        const { uid } = singleDocumentToBeDeleted.value;
        filesToBeDeleted.documentables.include.push(uid);
        spliceDocuments([uid]);
    } else {
        const { include, exclude } = selectedDocuments.value;
        filesToBeDeleted.documentables.include = [...include];
        filesToBeDeleted.documentables.exclude = [...exclude];

        if (include.includes('*')) {
            if (exclude.length > 0) {
                documents.value = documents.value.filter(doc => exclude.includes(doc.uid));
            } else {
                documents.value = [];
            }
        } else if (include.length > 0) {
            spliceDocuments(include);
        }
    }

    try {
        await deleteDocuments(organization.value.slug, filesToBeDeleted);
    } catch (e) {
        toast.error('An error occurred while deleting the document(s)');
    } finally {
        closeDeleteConfirmationModal();
        deleteLoading.value = false;
    }

    if(allDocumentsSelected.value) {
        allDocumentsSelected.value = false;
    }

    selectedDocuments.value = {
        include: [],
        exclude: []
    };
}

function closeDeleteConfirmationModal() {
    deleteModalVisibility.value = false;
    singleDocumentToBeDeleted.value = null;
}


async function handleAction(action, document = null, index, newValue) {
    switch(action) {
    case 'favorite':
        try {
            if(document.isLiked) {
                await removeFromFavorite(organization.value.slug, document.uid);
            } else {
                await addToFavorite(organization.value.slug, document.uid);
            }
            documents.value[index].isLiked = !documents.value[index].isLiked
        } catch (e) {
            toast.error('An error occurred while adding the document to favorites');
        }
        break;
    case 'select':
        if(allDocumentsSelected.value) {
            if(newValue) {
                selectedDocuments.value.exclude = selectedDocuments.value.exclude.filter(uid => uid !== document.uid);
            } else {
                selectedDocuments.value.exclude.push(document.uid);
            }
        } else {
            if(newValue) {
                selectedDocuments.value.include.push(document.uid);
            } else {
                selectedDocuments.value.include = selectedDocuments.value.include.filter(uid => uid !== document.uid);
            }
        }
        break;
    case 'download':
        handleDownload(document);
        break;
    case 'delete':
        deleteModalVisibility.value = true;
        singleDocumentToBeDeleted.value = document;
        break;
    case 'share':
        shareModalVisibility.value = true;
        singleDocumentToBeShared.value = document;
        break;
    case 'navigate':
        if(document.documentable_type === 'folder') {
            await navigateToFolder(document.documentable.uid);
        } else {
            await previewFile(document)
        }
        break;
    case 'move':
        moveDocumentModalVisibility.value = true;
        singleDocumentToBeMoved.value = document;
        break;
    default:
        // Handle unexpected cases
        console.log('Unknown action');
        break;
    }
}

const checkInViewport = () => {
    if (infiniteLoader.value) {
        return;
    }

    currentPage.value = currentPage.value + 1;

    loadMoreData();
}

function selectAllDocuments() {
    if(fileToBePreviewed.value) {
        return;
    }

    allDocumentsSelected.value = !allDocumentsSelected.value;

    if(allDocumentsSelected.value) {
        selectedDocuments.value.include = ['*']
        selectedDocuments.value.exclude = [];
    } else {
        selectedDocuments.value.include = [];
        selectedDocuments.value.exclude = [];
    }
}

function handleAfterLeaveShareModal () {
    singleDocumentToBeShared.value = null;
    shareModalVisibility.value = false;
    resetIncludeExclude();
}

async function handleShare({groups, members}) {
    shareLoading.value = true;

    const toBeSharedWith = {
        members: {
            include: [...members.include],
            exclude: [...members.exclude]
        },
        groups: {
            include: [...groups.include],
            exclude: [...groups.exclude]
        }
    }

    try {
        const response = await shareDocuments(organization.value.slug, singleDocumentToBeShared.value.uid, toBeSharedWith);

        const index = documents.value.findIndex(doc => doc.uid === singleDocumentToBeShared.value.uid);

        documents.value[index].members = [...response.members];
        documents.value[index].groups = [...response.groups];

        toast.success('Document shared successfully');
    } catch (e) {
        toast.error('An error occurred while sharing the document');
    } finally {
        shareModalVisibility.value = false;
        shareLoading.value = false;
        resetIncludeExclude();
    }
}

function closeMoveDocumentModal() {
    singleDocumentToBeMoved.value = null;
    moveDocumentModalVisibility.value = false;
}

function handleDownload(document) {
    downloadingFileLoader.value = true;
    try {
        downloadFile(organization.value.slug, document.documentable.filename, document.documentable.title ? document.documentable.title : document.documentable.filename);
    } catch(e) {
        toast.error('An error occurred while downloading the document');
    } finally {
        downloadingFileLoader.value = false;
    }
}

async function handleMoveDocument(targetFolder) {
    moveDocumentLoading.value = true;

    const include = singleDocumentToBeMoved.value ? [singleDocumentToBeMoved.value.uid] : [...selectedDocuments.value.include];

    try {
        const payload = {
            parent_documentable_uid: currentFolderDocumentableUid.value,
            documentables: {
                include: include,
                exclude: [...selectedDocuments.value.exclude]
            },
            move_to_documentable_uid: targetFolder.uid,
        };

        try {
            await moveDocuments(organization.value.slug, payload);

            toast.success('Document(s) moved successfully');
        } catch (e) {
            toast.error('An error occurred while moving the document(s)');
        }

        selectedDocuments.value = {
            include: [],
            exclude: []
        }

        await refreshData();

    } catch (e) {
        toast.error('An error occurred while moving the document(s)');
    } finally {
        moveDocumentLoading.value = false;
        closeMoveDocumentModal();
    }
}

const emptyState = computed(() => {
    return documents.value.length === 0 && !loadingDocuments.value;
});

const emptyStateText = computed(() => {
    if(hasDocuments.value) {
        if(sharedLinkUid !== '') {
            return 'The document does not exist or you do not have permissions to view it';
        }

        return 'We couldn\'t find any documents based on the searched filters';
    } else {
        return 'Looks like there are no documents added here yet';
    }
});

const emptyStateSubtitle = computed(() => {
    if(hasDocuments.value) {
        if(sharedLinkUid !== '') {
            return 'Double check the link received or contact the owner of the file in order to receive access';
        }
        return 'There will be documents that you can view, download or add to favorites';
    } else {
        return 'There will be documents that you can view, download or add to favorites';
    }
});

const currentFolder = computed(() => {
    const current = breadCrumbs.value[breadCrumbs.value.length - 1];

    if(breadCrumbs.value.length === 0 || DEFAULT_FOLDERS.includes(current.documentable.title)) {
        return null;
    }

    return current.documentable;
})

const currentFolderUid = computed(() => {
    const current = breadCrumbs.value[breadCrumbs.value.length - 1];
    const FavShareBin = ['favorite', 'share', 'bin']

    if(breadCrumbs.value.length === 0 || FavShareBin.includes(current.documentable.title)) {
        return null;
    }

    return current.documentable.uid;
})

const currentFolderDocumentableUid = computed(() => {
    if(breadCrumbs.value.length === 0) {
        return null;
    }

    return breadCrumbs.value[breadCrumbs.value.length - 1].uid;
})

const totalDocuments = computed(() => {
    if(meta.value) {
        return meta.value.total;
    }

    return 0;
})

onBeforeMount(async () => {
    if(sharedLinkUid !== '') {
        hasDocuments.value = true;

        // check if the shared link is a folder
        if(sharedLinkType === 'f') {
            await loadInitialData(sharedLinkUid);

            //todo: error handling cand nu exista fiserul sau nu e linkul bun/nu ai access

            if(documents.value.length > 0) {
                const parent = documents.value[0].documentable.parents;
                const keys = Object.keys(parent);
                const values = Object.values(parent);

                const auxBreadItem = {
                    documentable: {
                        title: values[0],
                        uid: keys[0]
                    }
                }

                breadCrumbs.value.push(auxBreadItem);
            }
        } else {
            const response = await showFile(organization.value.slug, sharedLinkUid);

            //todo: error handling cand nu exista fiserul sau nu e linkul bun/nu ai access

            if(response.data.length > 0) {
                const auxBreadItem = {
                    documentable: {
                        title: response.data[0].documentable.title,
                        uid: response.data[0].documentable.uid
                    },
                    uid: response.data.uid
                }

                breadCrumbs.value.push(auxBreadItem);

                fileToBePreviewed.value = response.data[0];
            }
        }
    } else {
        await loadInitialData();
        hasDocuments.value = documents.value.length > 0;
    }
})

</script>
<style scoped>
.grad-bg {
    background: rgb(255,255,255);
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%);
}
</style>